import cx from "classnames";
import { useState } from "react";
import { Checkbox, Flex, Input, Radio, Select, Text } from "@theorgchartdev/storybook";
import {
  chartPageSizes,
  fitScreenRadioProps,
  footerOptions,
  GetFooterIndex,
  GetHeaderIndex,
  GetMarginSizeOption,
  GetPageSizeOption,
  headerOptions,
  marginOptions,
  orientationRadioProps
} from "./PagePropertiesUtils";
import { GetDoc, GetWSMgr, ReRenderCanvas } from "@/Utils/StateLib";
import { chartModeTestId } from "@/appComponents/Navigation/RightSidebar/Utils/chartModeTestId";
import {
  attachChangeAndLocalizate,
  buildHeaderFooterProps,
  buildPageSizes,
  buildSelectProps,
  GetContentInches,
  GetDecString,
  GetUnitsString,
  marginKeys
} from "@/appComponents/Navigation/RightSidebar/PageProperties/utils";
import { useFormController } from "@/hooks/useFormController";
import { useTopic } from "@/hooks/useTopic";
import {
  enumUnitVal,
  enumPageSizeUnits,
  enumPageOrientationOptions,
  kDPIScreen
} from "@coreapp/constants";
import { Globals } from "@coreapp/CoreApp/Globals";
import { appstr } from "@coreapp/utils/appstr";
import { GetStdHeaderHeight } from "@coreapp/utils/GetStdHeaderHeight";
import { GetStdFooterHeight } from "@coreapp/utils/GetStdFooterHeight";
import { DoOptimizePages } from "@coreapp/utils/DoOptimizePages";
import { QRect } from "@coreapp/CoreApp/QRect";

export const PageProperties = props => {
  const manager = props?.doc?.GetPageMgr();
  const curDoc = manager?.GetDocMgr()?.GetCurrentDoc();
  const pagelayoutMgr = curDoc?.GetPageLayoutMgr();
  let wsMgr = GetWSMgr();
  const eventTracker = wsMgr.GetUsageTracker();

  const [units, setUnits] = useState(() => {
    if (localStorage.unitSystem !== null)
      return localStorage.unitSystem === "English" ? enumUnitVal.inches : enumUnitVal.cm;
  });

  const [pageSizeKey, setPageSizeKey] = useState(() => {
    const pageProps = manager?.GetPageProps();

    return GetPageSizeOption([pageProps.fPageHeight, pageProps.fPageWidth]);
  });

  const [marginsKey, setMarginsKey] = useState(() => {
    const pageProps = manager?.GetPageProps();
    const pageMargins = pageProps.fMargins;

    const marginsArray = [
      pageMargins["fBottom"],
      pageMargins["fLeft"],
      pageMargins["fRight"],
      pageMargins["fTop"]
    ];

    return GetMarginSizeOption(marginsArray);
  });

  const getHeaderKey = () => {
    const height = manager?.GetHeaderHeight();
    const headerIndex = GetHeaderIndex(height);
    return headerOptions[headerIndex];
  };

  const getFooterKey = () => {
    const height = manager?.GetFooterHeight();
    const footerIndex = GetFooterIndex(height);
    return footerOptions[footerIndex];
  };

  const [headerKey, setHeaderKey] = useState(() => {
    return getHeaderKey();
  });

  const [footerKey, setFooterKey] = useState(() => {
    return getFooterKey();
  });

  const getInitState = () => {
    const doc = GetDoc();
    const pageMgr = doc?.GetPageMgr();
    const margins = pageMgr?.GetPageMargins();
    const header = pageMgr?.GetHeaderHeight();
    const footer = pageMgr?.GetFooterHeight();
    const width = pageMgr?.GetMinPageWidth();
    const height = pageMgr?.GetMinPageHeight();
    return {
      header: GetDecString(header, units),
      footer: GetDecString(footer, units),
      marginTop: GetDecString(margins.fTop, units),
      marginRight: GetDecString(margins.fRight, units),
      marginLeft: GetDecString(margins.fLeft, units),
      marginBottom: GetDecString(margins.fBottom, units),
      pageWidth: GetDecString(width, units),
      pageHeight: GetDecString(height, units)
    };
  };

  const formController = useFormController(() => getInitState(), {
    onChange: (nextFieldData, updatedKey) => {
      updateCustomPageOptions(nextFieldData, updatedKey);
    }
  });

  const updateCustomPageOptions = (nextFieldData, updatedKey) => {
    const nextValue = nextFieldData[updatedKey];
    let isValid = false;
    if (updatedKey === "header") {
      isValid = UpdateCustomHeader(nextValue);
    } else if (updatedKey === "footer") {
      isValid = UpdateCustomFooter(nextValue);
    } else if (updatedKey.includes("page")) {
      isValid = UpdateCustomPageSize(updatedKey, nextValue);
    } else if (updatedKey.includes("margin")) {
      isValid = UpdateCustomMargin(updatedKey, nextValue);
    }
    if (isValid) ApplyProperties();
  };

  useTopic("PropertyChange", () => {
    formController.setFieldData(getInitState());
    setHeaderKey(getHeaderKey());
    setFooterKey(getFooterKey());
  });

  if (!manager) return;

  const ApplyProperties = () => {
    const wsMgr = GetWSMgr();
    const bgMgr = wsMgr.GetBGMgr();
    const canvasPanel = wsMgr.fCanvasPanel;
    const pageProps = manager.GetPageProps();
    canvasPanel.ChangePageProps(pageProps, enumPageSizeUnits.pixels);
    DoOptimizePages(wsMgr);
    canvasPanel.PanToTopCenter();
    ReRenderCanvas();
    wsMgr.BroadcastTopic("BoundaryChange");
    bgMgr.UpdateActiveBackground(null, pageProps);
  };

  const UpdatePageSize = value => {
    let sizeString = value;
    let sizeArray = chartPageSizes[value];
    const portrait = manager?.GetPageOrientation() === enumPageOrientationOptions.portrait;
    if (sizeString == "customps") {
      ChangePageSize(
        Math.round(GetContentInches(formController.fieldData["pageWidth"], units) * kDPIScreen),
        Math.round(GetContentInches(formController.fieldData["pageHeight"], units) * kDPIScreen),
        sizeString
      );
      return;
    }

    let pageWidth = sizeArray[1];
    let pageHeight = sizeArray[0];
    if (portrait) {
      pageHeight = sizeArray[1];
      pageWidth = sizeArray[0];
    }
    ChangePageSize(pageWidth, pageHeight, sizeString);
  };

  const ChangePageSize = (pageWidth, pageHeight, sizeString, apply = true) => {
    manager.SetMinPageSize(pageWidth, pageHeight);
    localStorage.pageSize = sizeString;
    setPageSizeKey(sizeString);
    if (apply) ApplyProperties();
    eventTracker.ReportEvent("chart_mode.page_size.saved", { page_size: sizeString });
  };

  const UpdateCustomPageSize = (key, value) => {
    const newValue = GetContentInches(value, units);
    const pixelValue = Math.round(newValue * kDPIScreen);
    let width = key === "pageWidth" ? pixelValue : manager?.GetMinPageWidth();
    let height = key === "pageHeight" ? pixelValue : manager?.GetMinPageHeight();
    if (width > 0 && height > 0) {
      ChangePageSize(width, height, "customps", false);
      return true;
    }
    return false;
  };

  const UpdateMargins = target => {
    let marginsString = target.value;
    let m = marginOptions[target.value];
    let marginsRect = new QRect();
    if (target.value == "customm") {
      marginsRect.InitRect(
        Math.round(GetContentInches(formController.fieldData["marginLeft"], units) * kDPIScreen),
        Math.round(GetContentInches(formController.fieldData["marginTop"], units) * kDPIScreen),
        Math.round(GetContentInches(formController.fieldData["marginRight"], units) * kDPIScreen),
        Math.round(GetContentInches(formController.fieldData["marginBottom"], units) * kDPIScreen)
      );
    } else {
      marginsRect.InitRect(m[0], m[1], m[2], m[3]);
    }
    manager.SetPageMargins(marginsRect);

    setMarginsKey(marginsString);
    ApplyProperties();
  };

  const UpdateCustomMargin = (key, value) => {
    let pageMargins = manager?.GetPageMargins();
    let nextMargins = pageMargins.Clone();
    const newValue = GetContentInches(value, units);
    let customMargin = Math.round(newValue * kDPIScreen);
    if (customMargin <= 0) customMargin = 1;
    if (customMargin > 0 && !isNaN(customMargin)) {
      const prop = marginKeys[key];
      nextMargins[prop] = customMargin;
      manager.SetPageMargins(nextMargins);
      return true;
    }
    return false;
  };

  const UpdateHeader = o => {
    const value = o.value;
    let headerHeight = 1;
    if (value === "standardh") headerHeight = GetStdHeaderHeight(enumUnitVal.pixels);
    else if (value == "customh")
      headerHeight = Math.round(
        GetContentInches(formController.fieldData["header"], units) * kDPIScreen
      );
    manager.SetHeaderHeight(headerHeight);
    setHeaderKey(value);
    ApplyProperties();
  };

  const UpdateCustomHeader = value => {
    const newValue = GetContentInches(value, units);
    let customHeight = Math.round(newValue * kDPIScreen);
    if (customHeight <= 0) customHeight = 1;
    if (customHeight > 0 && !isNaN(customHeight)) {
      manager.SetHeaderHeight(customHeight);
      return true;
    }
    return false;
  };

  const UpdateFooter = o => {
    const value = o.value;
    let footerHeight = 1;
    if (value === "standardf") footerHeight = GetStdFooterHeight(enumUnitVal.pixels);
    else if (value == "customf")
      footerHeight = Math.round(
        GetContentInches(formController.fieldData["footer"], units) * kDPIScreen
      );
    manager.SetFooterHeight(footerHeight);
    setFooterKey(value);
    ApplyProperties();
  };

  const UpdateCustomFooter = value => {
    const newValue = GetContentInches(value, units);
    let customHeight = Math.round(newValue * kDPIScreen);
    if (customHeight <= 0) customHeight = 1;
    if (customHeight > 0 && !isNaN(customHeight)) {
      manager.SetFooterHeight(customHeight);
      return true;
    }
    return false;
  };

  const UpdateWebView = event => {
    const newPP = event.target.value;
    const wsmgr = GetWSMgr();
    const canvasPanel = wsmgr.fCanvasPanel;
    pagelayoutMgr.SetFitToScreen(newPP === "true");
    localStorage.printPreview = newPP == "true" ? "false" : "true";
    if (newPP === "true") {
      canvasPanel.DoFitToScreen();
      const pageProps = manager.GetPageProps();
      const bgMgr = wsMgr.GetBGMgr();
      bgMgr.UpdateActiveBackground(null, pageProps);
      ReRenderCanvas();
    } else {
      UpdatePageSize(pageSizeKey);
      ApplyProperties();
    }
  };

  const UpdateOrientation = event => {
    let newOrientation = parseInt(event.target.value);

    let pageHeight, pageWidth;

    if (newOrientation !== manager.GetPageOrientation()) {
      pageHeight = manager.GetMinPageWidth();
      pageWidth = manager.GetMinPageHeight();
    }

    manager.SetMinPageSize(pageWidth, pageHeight);
    manager.SetPageOrientation(newOrientation);

    ApplyProperties();
    eventTracker.ReportEvent("chart_mode.page_orientation.saved", { orientation: newOrientation });
  };

  const baseClass = "ocn-sidebar-section-content";
  const dividerClass = "ocn-sidebar-section-content--divider";

  const editMode = Globals._uiConfig.InBackgroundEditorMode();

  const textProps = {
    element: "h2",
    modifier: "bold",
    size: "small"
  };

  const selectProps = {
    selectSize: "small",
    optionSize: "small"
  };

  const marginCheckboxProps = {
    id: "show-margins-checkbox",
    "data-testid": chartModeTestId("sidebar", "pp-show-margins-checkbox"),
    label: appstr("showmarg"),
    size: "small",
    checked: manager.GetShowPageMargins(),
    onToggle: () => {
      manager.SetShowPageMargins(!manager.GetShowPageMargins());
      ReRenderCanvas();
    }
  };

  const headerfooterCheckboxProps = {
    id: "show-headerfooter-checkbox",
    "data-testid": chartModeTestId("sidebar", "pp-show-headerfooter-checkbox"),
    label: appstr("showheadfoot"),
    size: "small",
    checked: manager.GetShowHeaderFooter(),
    onToggle: () => {
      const wsmgr = GetWSMgr();
      manager.SetShowHeaderFooter(!manager.GetShowHeaderFooter());
      wsmgr.BroadcastTopic("BoundaryChange");
      ReRenderCanvas();
    }
  };

  const selectPageSizeOptions = buildPageSizes(UpdatePageSize, chartPageSizes, "pp-page-size", {});
  const selectMarginProps = buildSelectProps(
    marginOptions,
    UpdateMargins,
    selectProps,
    "pp-margin"
  );
  const selectHeaderProps = buildHeaderFooterProps(
    headerOptions,
    UpdateHeader,
    selectProps,
    "pp-header"
  );
  const selectFooterProps = buildHeaderFooterProps(
    footerOptions,
    UpdateFooter,
    selectProps,
    "pp-footer"
  );
  const pageSizeFitScreenProps = attachChangeAndLocalizate(fitScreenRadioProps, UpdateWebView);
  const selectOrientationProps = attachChangeAndLocalizate(
    orientationRadioProps,
    UpdateOrientation
  );

  const isCustomMargin = marginsKey === "customm";
  const isCustomPageSize = pageSizeKey === "customps";
  const isCustomHeader = headerKey === "customh";
  const isCustomFooter = footerKey === "customf";
  const unitsString = GetUnitsString(units);

  const GetCustomStatus = key => {
    const value = formController.fieldData[key];
    if (!value) return "error";
    const decValue = GetContentInches(value, units);
    const valid = decValue >= 0 && !isNaN(decValue);
    if (!valid) return "error";
    return undefined;
  };

  const handleBlurUpdate = key => {
    switch (key) {
      case "pageSize":
        const pageHeight = manager?.GetMinPageHeight();
        const pageWidth = manager?.GetMinPageWidth();
        formController.onFieldChange("pageWidth", GetDecString(pageWidth, units));
        formController.onFieldChange("pageHeight", GetDecString(pageHeight, units));
        break;
      case "margin":
        const margins = manager?.GetPageMargins();
        formController.onFieldChange("marginTop", GetDecString(margins.fTop, units));
        formController.onFieldChange("marginRight", GetDecString(margins.fRight, units));
        formController.onFieldChange("marginBottom", GetDecString(margins.fBottom, units));
        formController.onFieldChange("marginLeft", GetDecString(margins.fLeft, units));
        break;
      case "header":
        formController.onFieldChange("header", GetDecString(manager?.GetHeaderHeight(), units));
        break;
      case "footer":
        formController.onFieldChange("footer", GetDecString(manager?.GetFooterHeight(), units));
        break;
    }
  };

  return (
    <div
      className={cx("ocn-sidebar-section-container", "oc-overflow-container", "ocn-list-scrollbar")}
    >
      <div className={cx(baseClass, "page-size")}>
        <Text {...textProps}>{appstr("Page size")}</Text>
        <Radio
          key="fit-to-screen"
          checked={pagelayoutMgr.FitToScreen()}
          data-testid={chartModeTestId("sidebar", "pp-fit-screen-radio")}
          {...pageSizeFitScreenProps[1]}
        />
        <Radio
          key="specify-page-size"
          checked={!pagelayoutMgr.FitToScreen()}
          data-testid={chartModeTestId("sidebar", "pp-specify-size-radio")}
          {...pageSizeFitScreenProps[0]}
        />
        <Flex direction={"column"} gap={"12px"} style={{ paddingLeft: "25px" }}>
          <Select
            dataTestId={"pp-page-size-select"}
            key="page-size"
            optionSize={"small"}
            value={pageSizeKey}
            disabled={pagelayoutMgr.FitToScreen()}
            options={selectPageSizeOptions}
            {...selectProps}
          />
          {isCustomPageSize && (
            <Flex gap={"8px"}>
              <Input
                id={"pp-page-width-input"}
                dataTestId={chartModeTestId("sidebar", "pp-page-width-input")}
                value={formController.fieldData["pageWidth"]}
                size={"small"}
                label={appstr("Width")}
                inputOnly={false}
                status={GetCustomStatus("pageWidth")}
                onChange={e => formController.onFieldChange("pageWidth", e.target.value)}
                disabled={!isCustomPageSize}
                units={unitsString}
                onBlur={() => handleBlurUpdate("pageSize")}
              />
              <Input
                id={"pp-page-height-input"}
                dataTestId={chartModeTestId("sidebar", "pp-page-height-input")}
                value={formController.fieldData["pageHeight"]}
                size={"small"}
                label={appstr("Height")}
                inputOnly={false}
                status={GetCustomStatus("pageHeight")}
                onChange={e => formController.onFieldChange("pageHeight", e.target.value)}
                disabled={!isCustomPageSize}
                units={unitsString}
                onBlur={() => handleBlurUpdate("pageSize")}
              />
            </Flex>
          )}
        </Flex>
      </div>
      {editMode && (
        <>
          <div className={cx(baseClass, dividerClass, "margin-size")}>
            <div className={"ocn-sidebar-section-content-gap"}>
              <Select
                dataTestId={"pp-margin-select"}
                inputOnly={false}
                label={appstr("Margin")}
                optionSize={"small"}
                key="margins"
                value={marginsKey}
                {...selectMarginProps}
              />
              <Flex direction={"column"}>
                {isCustomMargin && (
                  <Flex direction={"column"} gap={"4px"}>
                    <Flex gap={"8px"}>
                      <Input
                        id={"pp-margin-top-input"}
                        dataTestId={chartModeTestId("sidebar", "pp-margin-top-input")}
                        value={formController.fieldData["marginTop"]}
                        size={"small"}
                        label={appstr("Top")}
                        status={GetCustomStatus("marginTop")}
                        inputOnly={false}
                        onChange={e => formController.onFieldChange("marginTop", e.target.value)}
                        disabled={!isCustomMargin}
                        units={unitsString}
                        onBlur={() => handleBlurUpdate("margin")}
                      />
                      <Input
                        id={"pp-margin-right-input"}
                        dataTestId={chartModeTestId("sidebar", "pp-margin-right-input")}
                        value={formController.fieldData["marginRight"]}
                        size={"small"}
                        label={appstr("Right")}
                        inputOnly={false}
                        status={GetCustomStatus("marginRight")}
                        onChange={e => formController.onFieldChange("marginRight", e.target.value)}
                        disabled={!isCustomMargin}
                        units={unitsString}
                        onBlur={() => handleBlurUpdate("margin")}
                      />
                    </Flex>
                    <Flex gap={"8px"}>
                      <Input
                        id={"pp-margin-bottom-input"}
                        dataTestId={chartModeTestId("sidebar", "pp-margin-bottom-input")}
                        value={formController.fieldData["marginBottom"]}
                        size={"small"}
                        label={appstr("Bottom")}
                        inputOnly={false}
                        status={GetCustomStatus("marginBottom")}
                        onChange={e => formController.onFieldChange("marginBottom", e.target.value)}
                        disabled={!isCustomMargin}
                        units={unitsString}
                        onBlur={() => handleBlurUpdate("margin")}
                      />
                      <Input
                        id={"pp-margin-left-input"}
                        dataTestId={chartModeTestId("sidebar", "pp-margin-left-input")}
                        value={formController.fieldData["marginLeft"]}
                        size={"small"}
                        label={appstr("Left")}
                        onChange={e => formController.onFieldChange("marginLeft", e.target.value)}
                        inputOnly={false}
                        status={GetCustomStatus("marginLeft")}
                        disabled={!isCustomMargin}
                        units={unitsString}
                        onBlur={() => handleBlurUpdate("margin")}
                      />
                    </Flex>
                  </Flex>
                )}
                <Checkbox {...marginCheckboxProps} />
              </Flex>
              <Flex direction={"column"} gap={"4px"}>
                <div className={cx(baseClass, dividerClass, "margin-size")}>
                  <div className={"ocn-sidebar-section-content-gap"}>
                    <Flex direction={"column"}>
                      <Select
                        dataTestId={"pp-header-select"}
                        inputOnly={false}
                        label={appstr("Header")}
                        optionSize={"small"}
                        key="header-select"
                        value={headerKey}
                        {...selectHeaderProps}
                      />
                      {isCustomHeader && (
                        <Flex width={"50%"}>
                          <Input
                            id={"pp-header-input"}
                            dataTestId={chartModeTestId("sidebar", "pp-header-input")}
                            value={formController.fieldData["header"]}
                            size={"small"}
                            inputOnly={false}
                            status={GetCustomStatus("header")}
                            label={appstr("Height")}
                            onChange={e => formController.onFieldChange("header", e.target.value)}
                            disabled={!isCustomHeader}
                            units={unitsString}
                            onBlur={() => handleBlurUpdate("header")}
                          />
                        </Flex>
                      )}
                    </Flex>
                    <Flex direction={"column"}>
                      <Select
                        dataTestId={"pp-footer-select"}
                        inputOnly={false}
                        label={appstr("Footer")}
                        optionSize={"small"}
                        key="footer-select"
                        value={footerKey}
                        {...selectFooterProps}
                      />
                      {isCustomFooter && (
                        <Flex width={"50%"}>
                          <Input
                            id={"pp-footer-input"}
                            dataTestId={chartModeTestId("sidebar", "pp-footer-input")}
                            inputOnly={false}
                            status={GetCustomStatus("footer")}
                            label={appstr("Height")}
                            value={formController.fieldData["footer"]}
                            size={"small"}
                            onChange={e => formController.onFieldChange("footer", e.target.value)}
                            disabled={!isCustomFooter}
                            units={unitsString}
                            onBlur={() => handleBlurUpdate("footer")}
                          />
                        </Flex>
                      )}
                    </Flex>
                  </div>
                </div>
                <Checkbox {...headerfooterCheckboxProps} />
              </Flex>
            </div>
          </div>
          <div className={cx(baseClass, dividerClass, "orientation-select")}>
            <Text {...textProps}>{appstr("Orientation")}</Text>
            <Radio
              key="landscape-mode"
              checked={manager.GetPageOrientation() === 0}
              value={0}
              disabled={isCustomPageSize}
              data-testid={chartModeTestId("sidebar", "pp-landscape-radio")}
              {...selectOrientationProps[0]}
            />
            <Radio
              key="portrait-mode"
              checked={manager.GetPageOrientation() === 1}
              value={1}
              disabled={isCustomPageSize}
              data-testid={chartModeTestId("sidebar", "pp-portrait-radio")}
              {...selectOrientationProps[1]}
            />
          </div>
        </>
      )}
    </div>
  );
};
